import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { DATE_TIME_FORMATS } from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import {
  getInitials,
  getRootUrl,
  isToday,
  toFormattedDate,
} from "@miplatform/utils";
import { mdiAccountOutline, mdiArrowRightThin } from "@mdi/js";
import { Box, Typography } from "@mui/material";
import { MiAvatar } from "../../MiAvatar/MiAvatar";
import { MiMenu } from "../../MiMenu/MiMenu";
import { IPrioritySchedulerColumnHeaderProps } from "../PrioritySchedulerBoard.types";

const MENU_TYPES = {
  NONE: "none",
  INITIAL: "initial",
  VIEW: "view",
  STATUS: "status",
  SCHEDULING: "scheduling",
};

export function PrioritySchedulerColumnHeader({
  user,
  date,
  eventCount = 0,
  holdCount = 0,
  dateView = false,
  handleHeaderAction,
}: Readonly<IPrioritySchedulerColumnHeaderProps>) {
  const { classes, cx } = useStyles();
  const { navigate } = useRouter();
  const [menuType, setMenuType] = useState(MENU_TYPES.INITIAL);

  const onMenuOpen = () => {
    setMenuType(MENU_TYPES.INITIAL);
  };

  const onMenuClose = () => {
    setMenuType(MENU_TYPES.NONE);
  };

  const displayName =
    user?.displayName ?? `${user?.firstName} ${user?.lastName}`;
  const profilePictureRefId = user?.profilePicture?.referenceId;
  const profilePictureUrl = profilePictureRefId
    ? `${getRootUrl()}/${profilePictureRefId}`
    : null;

  const menuData = [];

  if (!dateView) {
    menuData.push(
      {
        label: "View",
        icon: mdiArrowRightThin,
        action: () => {
          setMenuType(MENU_TYPES.VIEW);
        },
        keepMenuOpen: true,
        hide: menuType !== MENU_TYPES.INITIAL,
      },
      {
        label: "View Profile",
        icon: mdiAccountOutline,
        action: () => {
          navigate(`/profile/${user?.id}`);
        },
        hide: menuType !== MENU_TYPES.VIEW,
      },
      {
        label: "View More",
        icon: mdiArrowRightThin,
        action: () => handleHeaderAction?.("viewMore", user?.id),
        hide: menuType !== MENU_TYPES.VIEW,
      }
    );
  }

  // TODO: RO - implement scheduling actions
  // {
  //   label: "Scheduling",
  //   icon: mdiCalendarClock,
  //   action: () => {
  //     setMenuType(MENU_TYPES.SCHEDULING);
  //   },
  //   keepMenuOpen: true,
  //   hide: menuType !== MENU_TYPES.INITIAL,
  // },
  // TODO: RO - implement future actions
  // {
  //   label: "Mark Inactive",
  //   icon: mdiClipboardAccountOutline,
  //   color: theme.palette.grey.medium,
  //   action: () => openMarkInactiveModal(user),
  //   // action: handleMarkInactiveModalOpen,
  //   hide: menuType !== MENU_TYPES.SCHEDULING || isBefore(date, getDate()),
  // },
  // {
  //   label: "Remove Inactive",
  //   icon: mdiClipboardAccountOutline,
  //   color: theme.palette.grey.medium,
  //   action: () => openRemoveInactiveModal(user),
  //   hide:
  //     menuType !== MENU_TYPES.SCHEDULING ||
  //     !user.availabilityLog?.some(
  //       (log) => log.type === AVAILABILITY_LOG_TYPE.INACTIVE
  //     ),
  // },
  // {
  //   label: "Clear Schedule",
  //   icon: mdiCancel,
  //   color: theme.palette.error.main,
  //   action: () => openClearScheduleModal(user),
  //   hide: menuType !== MENU_TYPES.SCHEDULING,
  // },

  return (
    <Box className={classes.headerContainer}>
      {dateView ? (
        <Box
          display="flex"
          flex={1}
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="HD1" noWrap textTransform="uppercase">
            {toFormattedDate(date, DATE_TIME_FORMATS.FORMAT_DDD)}
          </Typography>
          {isToday(date) ? (
            <Box display="flex" alignItems="center" justifyContent="center">
              <Typography
                className={classes.todayBadge}
                variant="BD1"
                noWrap
                color="info"
              >
                Today
              </Typography>
            </Box>
          ) : (
            <Typography variant="BD2" noWrap textTransform="uppercase">
              {toFormattedDate(date, DATE_TIME_FORMATS.FORMAT_MM_DD_YY)}
            </Typography>
          )}
        </Box>
      ) : (
        <Box className={classes.userDetails}>
          <MiAvatar
            className={classes.avatar}
            data-testid="user-initials"
            src={profilePictureUrl}
          >
            {getInitials(displayName)}
          </MiAvatar>
          <Typography variant="HD1" className={classes.titleText}>
            {displayName}
          </Typography>
        </Box>
      )}
      <Box className={classes.rightContentContainer}>
        {!!user && (
          <Box className={classes.badgesContainer}>
            {eventCount > 0 && (
              <Box className={cx(classes.countBadge)}>
                <Typography variant="BD1">{eventCount}</Typography>
              </Box>
            )}
            {holdCount > 0 && (
              <Box className={cx(classes.countBadge, classes.holdCount)}>
                <Typography variant="BD1">{holdCount}</Typography>
              </Box>
            )}
          </Box>
        )}
        {menuData.length > 0 && (
          <Box>
            <MiMenu
              data={menuData}
              condensed
              showCloseDropdown
              boldText
              onMenuOpen={onMenuOpen}
              closeContextMenu={onMenuClose}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles()((theme) => ({
  headerContainer: {
    display: "flex",
    flex: 1,
    gap: theme.spacing(1),
    overflow: "hidden",
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: theme.palette.common.white,
    padding: theme.spacing(1),
    borderBottom: `4px solid ${theme.palette.common.black}`,
    borderTopRightRadius: 4,
    borderTopLeftRadius: 4,
  },
  userDetails: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    gap: theme.spacing(1),
    overflow: "hidden",
  },
  rightContentContainer: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    flexShrink: 0,
  },
  todayBadge: {
    padding: theme.spacing(0, 0.5),
    border: `1px solid ${theme.palette.info.main}`,
    backgroundColor: theme.palette.info.light,
    borderRadius: 2,
  },
  avatar: {
    width: 16,
    height: 16,
    fontSize: theme.fontSize.xxsmall,
  },
  titleText: {
    color: theme.palette.bluegrey.dark,
    textWrap: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
  },
  badgesContainer: {
    display: "flex",
    gap: theme.spacing(0.5),
  },
  countBadge: {
    padding: theme.spacing(0, 0.5),
    borderRadius: 2,
    display: "flex",
    minWidth: 16,
    justifyContent: "space-around",
    alignItems: "center",
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.grey.darkAccent}`,
    backgroundColor: theme.palette.grey.mediumLight,
    fontSize: theme.fontSize.xsmall,
  },
  holdCount: {
    color: theme.palette.hold.main,
    borderColor: theme.palette.hold.main,
    backgroundColor: theme.palette.hold.light,
  },
}));
